import { ChangeEvent, useMemo } from 'react';

import { Checkbox, Input, Label, Radio, RadioGroup, Select, type TypeSelectOption } from '@thefarmersfront/kitchen';

import { useShallow } from 'zustand/react/shallow';

import { NumberUnitInput } from '~/components/common';

import {
  useGetCategoriesCodesParentCodeQuery,
  useGetCategoriesTypesTypeQuery,
  useProductModalOptions,
} from '~/hooks/product';
import { useProductStore } from '~/store';
import {
  formatNumber,
  transformToProductSelectOptions as transformToSelectOptions,
  validateSingleProductField,
} from '~/utils';

import { PRODUCT_LIMITS, SHELF_LIFE_NONE_DEFAULTS } from '~/constants';

import type { TypeProductModalRequiredInputState } from '~/types/product.types';

import styles from './ProductRegisterModalProductInfoSectionPolicy.module.scss';

const ProductRegisterModalInfoSectionPolicy = () => {
  const productModalOptions = useProductModalOptions();

  const [
    productRegisterModalValue,
    setProductRegisterModalValue,
    productModalRequiredValueStatus,
    setProductModalRequiredValueStatus,
    resetProductModalRequiredValueStatus,
    setFocusFieldRefs,
  ] = useProductStore(
    useShallow((state) => [
      state.productRegisterModalValue,
      state.setProductRegisterModalValue,
      state.productModalRequiredValueStatus,
      state.setProductModalRequiredValueStatus,
      state.resetProductModalRequiredValueStatus,
      state.setFocusFieldRefs,
    ])
  );

  const mainCategoryQuery = useGetCategoriesTypesTypeQuery({ type: 'MAIN', showGlobalDimOverlay: false });
  const mainCategoryOptions = mainCategoryQuery.data
    ? mainCategoryQuery.data.map(({ code, name }) => ({ value: code, text: name }))
    : [];

  // 중분류 카테고리 조회 (대분류 선택 시)
  const selectedMainCode = productRegisterModalValue.mainCategoryCode?.value;
  const middleCategoryQuery = useGetCategoriesCodesParentCodeQuery({
    parentCode: selectedMainCode ?? null,
    showGlobalDimOverlay: false,
  });

  // 소분류 카테고리 조회 (중분류 선택 시)
  const selectedMiddleCode = productRegisterModalValue.middleCategoryCode?.value;
  const thirdCategoryQuery = useGetCategoriesCodesParentCodeQuery({
    parentCode: selectedMiddleCode ?? null,
    showGlobalDimOverlay: false,
  });

  // 세분류 카테고리 조회 (소분류 선택 시)
  const selectedThirdCode = productRegisterModalValue.thirdCategoryCode?.value;
  const fourthCategoryQuery = useGetCategoriesCodesParentCodeQuery({
    parentCode: selectedThirdCode ?? null,
    showGlobalDimOverlay: false,
  });

  const optionMiddleCategory = useMemo(() => {
    if (!middleCategoryQuery.data) return [];
    return transformToSelectOptions(middleCategoryQuery.data);
  }, [middleCategoryQuery.data]);

  const optionThirdCategory = useMemo(() => {
    if (!thirdCategoryQuery.data) return [];
    return transformToSelectOptions(thirdCategoryQuery.data);
  }, [thirdCategoryQuery.data]);

  const optionFourthCategory = useMemo(() => {
    if (!fourthCategoryQuery.data) return [];
    return transformToSelectOptions(fourthCategoryQuery.data);
  }, [fourthCategoryQuery.data]);

  const isShelfLifeNone = productRegisterModalValue.shelfLifeType?.value === 'NONE';

  const handleChangeShelfLifeTypeSelect = (_: unknown, value: TypeSelectOption<string>) => {
    const isNone = value.value === 'NONE';

    setProductRegisterModalValue({
      ...productRegisterModalValue,
      shelfLifeType: value,
      availableDays: isNone ? SHELF_LIFE_NONE_DEFAULTS.AVAILABLE_DAYS : null,
      sellAvailableDays: isNone ? SHELF_LIFE_NONE_DEFAULTS.SELL_AVAILABLE_DAYS : null,
      sellDeadlineDays: isNone ? SHELF_LIFE_NONE_DEFAULTS.SELL_DEADLINE_DAYS : null,
    });

    resetProductModalRequiredValueStatus('shelfLifeType');
    resetProductModalRequiredValueStatus('availableDays');
    resetProductModalRequiredValueStatus('sellAvailableDays');
    resetProductModalRequiredValueStatus('sellDeadlineDays');
  };

  const handleClickRequiredInput = (id: string) => {
    resetProductModalRequiredValueStatus(id as keyof TypeProductModalRequiredInputState);
  };

  const handleChangeDaysInput = (e: ChangeEvent<HTMLInputElement>) => {
    const id = e.currentTarget.id as keyof TypeProductModalRequiredInputState;
    const value = e.target.value === '' ? null : e.target.value;

    setProductRegisterModalValue({
      ...productRegisterModalValue,
      [id]: value,
    });

    const validationResult = validateSingleProductField(id, value);

    setProductModalRequiredValueStatus({
      ...productModalRequiredValueStatus,
      [id]: validationResult,
    });
  };

  const handleChangeKeepLevelSelect = (_: unknown, value: TypeSelectOption<string>) => {
    setProductRegisterModalValue({
      ...productRegisterModalValue,
      temperatureType: value,
    });

    resetProductModalRequiredValueStatus('temperatureType');
  };

  const handleChangeMainCategorySelect = (_: unknown, value: TypeSelectOption<string>) => {
    if (productRegisterModalValue.mainCategoryCode === value) {
      return;
    }

    setProductRegisterModalValue({
      ...productRegisterModalValue,
      mainCategoryCode: value,
      middleCategoryCode: null,
      thirdCategoryCode: null,
      fourthCategoryCode: null,
    });

    resetProductModalRequiredValueStatus('mainCategoryCode');
  };

  const handleChangeMiddleCategorySelect = (_: unknown, value: TypeSelectOption<string>) => {
    if (productRegisterModalValue.middleCategoryCode === value) {
      return;
    }

    setProductRegisterModalValue({
      ...productRegisterModalValue,
      middleCategoryCode: value,
      thirdCategoryCode: null,
      fourthCategoryCode: null,
    });

    resetProductModalRequiredValueStatus('middleCategoryCode');
  };

  const handleChangeThirdCategorySelect = (_: unknown, value: TypeSelectOption<string>) => {
    if (productRegisterModalValue.thirdCategoryCode === value) {
      return;
    }

    setProductRegisterModalValue({
      ...productRegisterModalValue,
      thirdCategoryCode: value,
      fourthCategoryCode: null,
    });

    resetProductModalRequiredValueStatus('thirdCategoryCode');
  };

  const handleChangeFourthCategorySelect = (_: unknown, value: TypeSelectOption<string>) => {
    setProductRegisterModalValue({
      ...productRegisterModalValue,
      thirdCategoryCode: value,
    });
  };

  const handleChangeIsOneDayProductRadio = (e: ChangeEvent<HTMLInputElement>) => {
    setProductRegisterModalValue({
      ...productRegisterModalValue,
      isOneDayProduct: e.target.value === 'true',
    });
  };

  const handleChangeCheckbox = (e: ChangeEvent<HTMLInputElement>) => {
    setProductRegisterModalValue({
      ...productRegisterModalValue,
      [e.target.id]: e.currentTarget.checked,
    });
  };

  const handleChangeRefrigerantRadio = (e: ChangeEvent<HTMLInputElement>) => {
    setProductRegisterModalValue({
      ...productRegisterModalValue,
      isNeedRefrigerant: e.target.value === 'true',
    });

    resetProductModalRequiredValueStatus('isNeedRefrigerant');
  };

  return (
    <>
      <div className={styles['item-row']}>
        <Label className={styles['label']} title="기한정책" required />
        <div className={styles['deadline-policy-wrapper']}>
          <Select
            ref={(ref) => setFocusFieldRefs('shelfLifeType', ref)}
            id="shelfLifeType"
            className={styles['select']}
            size="small"
            options={productModalOptions?.shelfLifeType ?? []}
            value={productRegisterModalValue.shelfLifeType}
            status={productModalRequiredValueStatus.shelfLifeType.status}
            validationMessage={productModalRequiredValueStatus.shelfLifeType.message}
            onChange={handleChangeShelfLifeTypeSelect}
          />
          <NumberUnitInput
            ref={(ref) => setFocusFieldRefs('availableDays', ref)}
            id="availableDays"
            className={styles['input']}
            disabled={isShelfLifeNone}
            value={productRegisterModalValue.availableDays ?? ''}
            size="small"
            unit="일"
            placeholder={`${formatNumber(PRODUCT_LIMITS.INPUT_VALUES.POLICY_DAYS)} 이하 숫자만 입력`}
            status={productModalRequiredValueStatus.availableDays.status}
            validationMessage={productModalRequiredValueStatus.availableDays.message}
            onClick={() => handleClickRequiredInput('availableDays')}
            onChange={handleChangeDaysInput}
          />
        </div>
      </div>

      <div className={styles['item-row']}>
        <Label className={styles['label']} title="판매가능일수" required />
        <div className={styles['input-wrapper']}>
          <NumberUnitInput
            id="sellAvailableDays"
            ref={(ref) => setFocusFieldRefs('sellAvailableDays', ref)}
            className={styles['input']}
            disabled={isShelfLifeNone}
            value={productRegisterModalValue.sellAvailableDays ?? ''}
            size="small"
            unit="일"
            placeholder="숫자만 입력"
            status={productModalRequiredValueStatus.sellAvailableDays.status}
            validationMessage={productModalRequiredValueStatus.sellAvailableDays.message}
            onClick={() => handleClickRequiredInput('sellAvailableDays')}
            onChange={handleChangeDaysInput}
          />
        </div>
      </div>

      <div className={styles['item-row']}>
        <Label className={styles['label']} title="판매마감일수" required />
        <div className={styles['input-wrapper']}>
          <NumberUnitInput
            id="sellDeadlineDays"
            ref={(ref) => setFocusFieldRefs('sellDeadlineDays', ref)}
            className={styles['input']}
            disabled={isShelfLifeNone}
            value={productRegisterModalValue.sellDeadlineDays ?? ''}
            size="small"
            unit="일"
            placeholder="숫자만 입력"
            status={productModalRequiredValueStatus.sellDeadlineDays.status}
            validationMessage={productModalRequiredValueStatus.sellDeadlineDays.message}
            onClick={() => handleClickRequiredInput('sellDeadlineDays')}
            onChange={handleChangeDaysInput}
          />
        </div>
      </div>

      <div className={styles['item-row']}>
        <Label className={styles['label']} title="보관유형" required />
        <div className={styles['input-wrapper']}>
          <Select
            ref={(ref) => setFocusFieldRefs('temperatureType', ref)}
            id="temperatureType"
            size="small"
            options={productModalOptions?.keepLevel ?? []}
            value={productRegisterModalValue.temperatureType}
            placeholder="선택"
            status={productModalRequiredValueStatus.temperatureType.status}
            validationMessage={productModalRequiredValueStatus.temperatureType.message}
            onClick={() => handleClickRequiredInput('temperatureType')}
            onChange={handleChangeKeepLevelSelect}
          />
        </div>
      </div>

      <div className={styles['item-row']}>
        <Label className={styles['label']} title="분류" required />
        <div className={styles['categories-select-wrapper']}>
          <Select
            ref={(ref) => setFocusFieldRefs('mainCategoryCode', ref)}
            id="mainCategoryCode"
            size="small"
            options={mainCategoryOptions}
            value={productRegisterModalValue.mainCategoryCode}
            disabled={!mainCategoryOptions.length}
            placeholder="대분류 선택"
            status={productModalRequiredValueStatus.mainCategoryCode.status}
            validationMessage={productModalRequiredValueStatus.mainCategoryCode.message}
            onClick={() => handleClickRequiredInput('mainCategoryCode')}
            onChange={handleChangeMainCategorySelect}
          />
          <Select
            ref={(ref) => setFocusFieldRefs('middleCategoryCode', ref)}
            id="middleCategoryCode"
            size="small"
            options={optionMiddleCategory}
            value={productRegisterModalValue.middleCategoryCode}
            disabled={!optionMiddleCategory.length}
            placeholder="중분류 선택"
            status={productModalRequiredValueStatus.middleCategoryCode.status}
            validationMessage={productModalRequiredValueStatus.middleCategoryCode.message}
            onClick={() => handleClickRequiredInput('middleCategoryCode')}
            onChange={handleChangeMiddleCategorySelect}
          />
          <Select
            ref={(ref) => setFocusFieldRefs('thirdCategoryCode', ref)}
            id="thirdCategoryCode"
            size="small"
            options={optionThirdCategory}
            value={productRegisterModalValue.thirdCategoryCode}
            disabled={!optionThirdCategory.length}
            placeholder="소분류 선택"
            status={productModalRequiredValueStatus.thirdCategoryCode.status}
            validationMessage={productModalRequiredValueStatus.thirdCategoryCode.message}
            onClick={() => handleClickRequiredInput('thirdCategoryCode')}
            onChange={handleChangeThirdCategorySelect}
          />
          <Select
            id="fourthCategoryCode"
            size="small"
            options={optionFourthCategory}
            value={productRegisterModalValue.fourthCategoryCode}
            onChange={handleChangeFourthCategorySelect}
            disabled={!optionFourthCategory.length}
            placeholder="세분류 선택"
          />
        </div>
      </div>

      <div className={styles['item-row']}>
        <Label className={styles['label']} title="일판매여부" />
        <div className={`${styles['input-wrapper']} ${styles['radio-group']}`}>
          <RadioGroup
            name="is-one-day-product"
            value={productRegisterModalValue.isOneDayProduct.toString()}
            size="medium"
            onChange={handleChangeIsOneDayProductRadio}
          >
            <Radio value="true" label="예" />
            <Radio value="false" label="아니오" />
          </RadioGroup>
        </div>
      </div>

      <div className={styles['item-row']}>
        <Label className={styles['label']} title="판매방법" />
        <div className={`${styles['input-wrapper']} ${styles['radio-group']}`}>
          <Input
            className={styles['input']}
            id="sellingType"
            value={
              productModalOptions?.sellingType.find(({ value }) => value === productRegisterModalValue.sellingType)
                ?.text ?? ''
            }
            name="sellingType"
            type="text"
            size="small"
            disabled
          />
        </div>
      </div>

      <div className={styles['item-row']}>
        <Label className={styles['label']} title="유의성질여부" />
        <div className={styles['caution-checkbox-wrapper']}>
          <Checkbox
            id="isFragile"
            className={styles['checkbox-item']}
            label="유리포함"
            checked={productRegisterModalValue.isFragile}
            size="medium"
            onChange={handleChangeCheckbox}
          />
          <Checkbox
            id="isWettable"
            className={styles['checkbox-item']}
            label="눌림주의"
            checked={productRegisterModalValue.isWettable}
            size="medium"
            onChange={handleChangeCheckbox}
          />
        </div>
      </div>

      <div ref={(ref) => setFocusFieldRefs('isNeedRefrigerant', ref)} className={styles['item-row']}>
        <Label className={styles['label']} title="컬리냉매제" required />
        <div className={`${styles['input-wrapper']} ${styles['radio-group']}`}>
          <RadioGroup
            name="refrigerant"
            value={productRegisterModalValue.isNeedRefrigerant.toString()}
            size="medium"
            status={productModalRequiredValueStatus.isNeedRefrigerant.status}
            validationMessage={productModalRequiredValueStatus.isNeedRefrigerant.message}
            onChange={handleChangeRefrigerantRadio}
          >
            <Radio value="true" label="냉매제 필요" />
            <Radio value="false" label="냉매제 필요없음" />
          </RadioGroup>
        </div>
      </div>
    </>
  );
};

export default ProductRegisterModalInfoSectionPolicy;
